import { useQuery } from '@tanstack/react-query';
import { publicPost } from '@/lib/api/publicClient';
import { publicBookingEndpoints } from '@/lib/api/endpoints';
import type { BookingQuote } from '@/types/onlineBooking';

export const bookingQuoteKeys = {
  all: () => ['public', 'booking-quote'] as const,
  // The FULL address is part of the key — tax resolution can differ by
  // city/state (not only zip), so a cached total must never be reused for a
  // different address.
  forAddress: (
    key: string,
    serviceId: string,
    address: { street: string; city: string; state: string; zip: string },
    selectedOptionalIds: string[],
  ) =>
    [
      ...bookingQuoteKeys.all(),
      key,
      serviceId,
      address.zip,
      address.city,
      address.state,
      address.street,
      ...selectedOptionalIds,
    ] as const,
};

/**
 * Itemized pricebook quote (with tax) for a service at the customer's address.
 * Enabled as soon as the zip is known — it warms while the customer browses
 * slots so the confirm-step order summary renders instantly. Informational
 * only: errors never block booking (the UI falls back to the flat price).
 * Disabled entirely when the link hides prices (no amounts on the wire).
 */
export function useBookingQuote(params: {
  linkKey: string;
  serviceId: string | undefined;
  hasPricebookItems: boolean;
  showPrices: boolean;
  address: { street: string; city: string; state: string; zip: string };
  selectedOptionalLineItemIds: string[];
}) {
  const {
    linkKey,
    serviceId,
    hasPricebookItems,
    showPrices,
    address,
    selectedOptionalLineItemIds,
  } = params;
  return useQuery<BookingQuote>({
    queryKey: bookingQuoteKeys.forAddress(
      linkKey,
      serviceId ?? '',
      address,
      selectedOptionalLineItemIds,
    ),
    queryFn: () =>
      publicPost<BookingQuote>(publicBookingEndpoints.quote(linkKey, serviceId!), {
        zip: address.zip,
        street: address.street || undefined,
        city: address.city || undefined,
        state: address.state || undefined,
        selectedOptionalLineItemIds: selectedOptionalLineItemIds.length
          ? selectedOptionalLineItemIds
          : undefined,
      }),
    enabled: Boolean(linkKey && serviceId && hasPricebookItems && showPrices && address.zip.trim()),
    staleTime: 2 * 60_000,
    retry: 1,
  });
}
